import type { AttendanceSettings, AttendanceSettingsUpdate } from '@erp/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, FormField, Input, Skeleton } from '../../../components/ui/index.js';
import { attendanceErrorCode, getAttendanceSettings, saveAttendanceSettings } from '../../../lib/attendance.js';

/** The zones the shops on this platform actually run in; the current value and the browser's own are added. */
const ZONES = [
  'Asia/Baghdad', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Dubai', 'Asia/Tehran', 'Europe/Istanbul',
  'Asia/Amman', 'Asia/Damascus', 'Asia/Beirut', 'Africa/Cairo', 'Europe/Berlin', 'Europe/London', 'UTC',
];

function Toggle({ id, label, hint, checked, onChange }: {
  id: string; label: string; hint: string; checked: boolean; onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-sm">
      <input id={id} type="checkbox" className="mt-0.5 h-4 w-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="font-medium">{label}</span>
        <span className="block text-xs text-fg-muted">{hint}</span>
      </span>
    </label>
  );
}

/** The shop's clock, when judging began, and who may punch where. Payroll policy lives with payroll. */
export function AttendanceSettingsPanel(): JSX.Element {
  const { t } = useTranslation('hr');
  const [saved, setSaved] = useState<AttendanceSettings | null>(null);
  const [form, setForm] = useState<AttendanceSettingsUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void getAttendanceSettings()
      .then((s) => { setSaved(s); setForm(s); })
      .catch(() => toast.error(t('attendance.errors.generic')));
  }, [t]);

  const zones = useMemo(() => {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return [...new Set([saved?.timezone, browser, ...ZONES].filter((z): z is string => Boolean(z)))];
  }, [saved?.timezone]);

  if (!saved) return <Card className="p-4"><Skeleton className="h-32 w-full" /></Card>;

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const next = await saveAttendanceSettings({
        timezone: form.timezone,
        trackFrom: form.trackFrom || null,
        selfServiceEnabled: form.selfServiceEnabled,
        kioskRequirePin: form.kioskRequirePin,
      });
      setSaved(next);
      setForm(next);
      toast.success(t('attendance.sessions.saved'));
    } catch (err) {
      const code = attendanceErrorCode(err);
      toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="flex max-w-3xl flex-col gap-5 p-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField id="as-tz" label={t('attendance.settings.timezone')} helperText={t('attendance.settings.timezoneHint')}>
          <select
            id="as-tz"
            value={form.timezone ?? saved.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            dir="ltr"
          >
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </FormField>
        <FormField id="as-from" label={t('attendance.settings.trackFrom')} helperText={t('attendance.settings.trackFromHint')}>
          <Input
            id="as-from"
            type="date"
            value={form.trackFrom ?? ''}
            onChange={(e) => setForm({ ...form, trackFrom: e.target.value || null })}
          />
        </FormField>
      </div>
      <div className="flex flex-col gap-3">
        <Toggle
          id="as-self"
          label={t('attendance.settings.selfService')}
          hint={t('attendance.settings.selfServiceHint')}
          checked={form.selfServiceEnabled ?? saved.selfServiceEnabled}
          onChange={(v) => setForm({ ...form, selfServiceEnabled: v })}
        />
        <Toggle
          id="as-pin"
          label={t('attendance.settings.kioskPin')}
          hint={t('attendance.settings.kioskPinHint')}
          checked={form.kioskRequirePin ?? saved.kioskRequirePin}
          onChange={(v) => setForm({ ...form, kioskRequirePin: v })}
        />
      </div>
      <div>
        <Button onClick={() => void save()} loading={saving}>{t('attendance.sessions.save')}</Button>
      </div>
    </Card>
  );
}
